import type { Ref } from 'vue'

/**
 * The template's "fade up on scroll" motion, without pulling in an animation
 * library: every `[data-reveal]` element starts shifted down and transparent
 * (see `assets/styles/modules/sofax.scss`) and gets `is-revealed` once it
 * enters the viewport. Stagger a group with `style="--reveal-delay: 120ms"`.
 *
 * Elements that appear later (behind a tab, a `v-if`) are picked up by calling
 * the returned `refresh()` after `nextTick`.
 *
 * With reduced motion, or without IntersectionObserver, everything is revealed
 * straight away rather than left invisible.
 */
export function useScrollReveal(root?: Ref<HTMLElement | null>) {
  let observer: IntersectionObserver | null = null

  const pending = (scope: ParentNode) =>
    Array.from(scope.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-revealed)'))

  function scan() {
    if (!import.meta.client) return

    const scope: ParentNode | null = root ? root.value : document
    if (!scope) return

    for (const el of pending(scope)) {
      if (observer) observer.observe(el)
      else el.classList.add('is-revealed')
    }
  }

  onMounted(() => {
    const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (motionOk && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            entry.target.classList.add('is-revealed')
            observer?.unobserve(entry.target)
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
      )
    }

    nextTick(scan)
  })

  onUnmounted(() => {
    observer?.disconnect()
    observer = null
  })

  return {
    refresh: () => nextTick(scan),
  }
}
